import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  NotificationCoverage,
  UserNotificationTypeUpdateDto,
  UserNotificationTypesDto,
  useResetUserNotificationTypes,
  useSaveUserNotificationTypes,
  useUserNotificationTypes,
} from "../../../api/notifications-api";
import Modal from "../../../components/Modal";
import Icon from "../../Icon";
import styles from "./NotificationDropdown.module.css";

type NotificationSettingsProps = {
  customer?: string;
  isOpen: boolean;
  onClose: () => void;
};

type NotificationSettingNode = {
  id: string;
  label: string;
  notificationTypeId?: number;
  coverage?: NotificationCoverage;
  push?: boolean;
  children?: NotificationSettingNode[];
};

type SettingState = "enabled" | "partial" | "disabled";

type FlatNotificationSettingItem = {
  id: string;
  label: string;
  level: number;
  ancestorIds: string[];
  hasChildren: boolean;
  notificationTypeId?: number;
  coverage?: NotificationCoverage;
  push?: boolean;
};

function mapCoverageToSettingState(
  coverage: NotificationCoverage
): SettingState {
  if (coverage === 2 || coverage === "all") return "enabled";
  if (coverage === 1 || coverage === "partial") return "partial";
  return "disabled";
}

function isUserOverrideSource(source?: number | string): boolean {
  if (source === 1) {
    return true;
  }

  if (typeof source === "string") {
    return source.toLowerCase() === "useroverride";
  }

  return false;
}

function buildNotificationSettingItems(
  data?: UserNotificationTypesDto
): NotificationSettingNode[] {
  if (!data?.NotificationTypes?.length) {
    return [];
  }

  const grouped = new Map<string, NotificationSettingNode>();

  data.NotificationTypes.forEach((notificationType) => {
    const category = notificationType.Category || "Other";

    if (!grouped.has(category)) {
      grouped.set(category, {
        id: `category-${category}`,
        label: category,
        children: [],
      });
    }

    grouped.get(category)?.children?.push({
      id: `type-${notificationType.NotificationTypeId}`,
      label: notificationType.NotificationTypeName,
      notificationTypeId: notificationType.NotificationTypeId,
      coverage: notificationType.Coverage,
      push: notificationType.Push,
    });
  });

  return Array.from(grouped.values());
}

function flattenNotificationSettingItems(
  items: NotificationSettingNode[],
  level = 1,
  ancestorIds: string[] = []
): FlatNotificationSettingItem[] {
  return items.flatMap((item) => {
    const flatItem: FlatNotificationSettingItem = {
      id: item.id,
      label: item.label,
      level,
      ancestorIds,
      hasChildren: Boolean(item.children?.length),
      notificationTypeId: item.notificationTypeId,
      coverage: item.coverage,
      push: item.push,
    };

    const children = item.children
      ? flattenNotificationSettingItems(item.children, level + 1, [
          ...ancestorIds,
          item.id,
        ])
      : [];

    return [flatItem, ...children];
  });
}

function buildDescendantIdsById(items: NotificationSettingNode[]) {
  const descendantsById: Record<string, string[]> = {};

  const walk = (node: NotificationSettingNode): string[] => {
    const descendantIds = (node.children ?? []).flatMap((child) => {
      const nestedDescendants = walk(child);
      return [child.id, ...nestedDescendants];
    });

    descendantsById[node.id] = descendantIds;
    return descendantIds;
  };

  items.forEach((item) => walk(item));

  return descendantsById;
}

function deriveParentSettingStates(
  settings: Record<string, SettingState>,
  items: NotificationSettingNode[]
): Record<string, SettingState> {
  const updated = { ...settings };

  const walk = (node: NotificationSettingNode): SettingState => {
    if (!node.children || node.children.length === 0) {
      return updated[node.id] ?? "disabled";
    }

    const childStates = node.children.map((child) => walk(child));

    const nextState = childStates.every((state) => state === "enabled")
      ? "enabled"
      : childStates.every((state) => state === "disabled")
        ? "disabled"
        : "partial";

    updated[node.id] = nextState;
    return nextState;
  };

  items.forEach((item) => {
    walk(item);
  });

  return updated;
}

function NotificationSettings({
  customer,
  isOpen,
  onClose,
}: NotificationSettingsProps) {
  const notificationTypesQuery = useUserNotificationTypes(customer, isOpen);
  const notificationSettingItems = buildNotificationSettingItems(
    notificationTypesQuery.data
  );
  const flatNotificationSettingItems = flattenNotificationSettingItems(
    notificationSettingItems
  );
  const settingDescendantIdsById = buildDescendantIdsById(
    notificationSettingItems
  );
  const leafSettingIds = flatNotificationSettingItems
    .filter((item) => !item.hasChildren)
    .map((item) => item.id);

  const [notificationSettings, setNotificationSettings] = useState<
    Record<string, SettingState>
  >({});
  const [pushSettings, setPushSettings] = useState<Record<string, boolean>>({});
  const [initialSettingsSnapshot, setInitialSettingsSnapshot] = useState("");
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  const saveNotificationTypesMutation = useSaveUserNotificationTypes(customer);
  const resetNotificationTypesMutation =
    useResetUserNotificationTypes(customer);

  const createSettingsSnapshot = (
    settings: Record<string, SettingState>,
    push: Record<string, boolean>
  ) =>
    JSON.stringify({
      settings: Object.keys(settings)
        .sort()
        .map((key) => [key, settings[key]]),
      push: Object.keys(push)
        .sort()
        .map((key) => [key, push[key]]),
    });

  const buildNotificationTypeUpdates = (
    settings: Record<string, SettingState>,
    push: Record<string, boolean>
  ): UserNotificationTypeUpdateDto[] =>
    flatNotificationSettingItems
      .filter((item) => !item.hasChildren && item.notificationTypeId != null)
      .map((item) => {
        const enabled = settings[item.id] !== "disabled";

        return {
          NotificationTypeId: item.notificationTypeId as number,
          Enabled: enabled,
          Push: enabled ? Boolean(push[item.id]) : false,
        };
      });

  const applyServerData = (serverData?: UserNotificationTypesDto) => {
    if (!serverData) {
      return;
    }

    const itemsFromServer = buildNotificationSettingItems(serverData);
    const flatItemsFromServer =
      flattenNotificationSettingItems(itemsFromServer);
    const baseSettings = Object.fromEntries(
      flatItemsFromServer.map((item) => {
        if (item.hasChildren) {
          return [item.id, "enabled"] as const;
        }

        return [
          item.id,
          mapCoverageToSettingState(item.coverage ?? 0),
        ] as const;
      })
    );

    const nextNotificationSettings = deriveParentSettingStates(
      baseSettings,
      itemsFromServer
    );
    const nextPushSettings = Object.fromEntries(
      flatItemsFromServer
        .filter((item) => !item.hasChildren)
        .map((item) => [item.id, item.push ?? false])
    );

    setNotificationSettings(nextNotificationSettings);
    setPushSettings(nextPushSettings);
    setInitialSettingsSnapshot(
      createSettingsSnapshot(nextNotificationSettings, nextPushSettings)
    );
  };

  useEffect(() => {
    if (isOpen && notificationTypesQuery.data && !initialSettingsSnapshot) {
      applyServerData(notificationTypesQuery.data);
    }
  }, [isOpen, notificationTypesQuery.data, initialSettingsSnapshot]);

  const allSettings = Object.values(notificationSettings);
  const allNotificationsState: SettingState =
    allSettings.length === 0
      ? "disabled"
      : allSettings.every((state) => state === "enabled")
        ? "enabled"
        : allSettings.every((state) => state === "disabled")
          ? "disabled"
          : "partial";
  const allNotificationsSubtitle =
    allNotificationsState === "partial"
      ? "Some notifications are partially enabled"
      : undefined;
  const canResetToDefault = isUserOverrideSource(
    notificationTypesQuery.data?.Source
  );
  const currentSettingsSnapshot = useMemo(
    () => createSettingsSnapshot(notificationSettings, pushSettings),
    [notificationSettings, pushSettings]
  );
  const hasPendingChanges =
    Boolean(initialSettingsSnapshot) &&
    currentSettingsSnapshot !== initialSettingsSnapshot;

  useEffect(() => {
    if (!isOpen) {
      setNotificationSettings({});
      setPushSettings({});
      setInitialSettingsSnapshot("");
    }
  }, [isOpen]);

  const handleToggleAllNotifications = () => {
    const nextState: SettingState =
      allNotificationsState === "enabled" ? "disabled" : "enabled";
    const nextSettings = deriveParentSettingStates(
      Object.fromEntries(
        flatNotificationSettingItems.map((item) => [item.id, nextState])
      ),
      notificationSettingItems
    );
    setNotificationSettings(nextSettings);

    const nextPush =
      nextState === "disabled"
        ? Object.fromEntries(leafSettingIds.map((id) => [id, false]))
        : Object.fromEntries(leafSettingIds.map((id) => [id, true]));

    setPushSettings(nextPush);
  };

  const handleToggleNotificationSetting = (settingId: string) => {
    setNotificationSettings((current) => {
      const currentState = current[settingId] ?? "disabled";
      const nextState: SettingState =
        currentState === "enabled" ? "disabled" : "enabled";
      const updated = {
        ...current,
        [settingId]: nextState,
      };

      const descendantIds = settingDescendantIdsById[settingId] ?? [];
      descendantIds.forEach((descendantId) => {
        updated[descendantId] = nextState;
      });

      const nextSettings = deriveParentSettingStates(
        updated,
        notificationSettingItems
      );

      setPushSettings((currentPushSettings) => {
        const nextPushSettings = { ...currentPushSettings };
        leafSettingIds.forEach((leafId) => {
          if (nextSettings[leafId] === "disabled") {
            nextPushSettings[leafId] = false;
          } else if (
            nextSettings[leafId] === "enabled" &&
            current[leafId] !== "enabled"
          ) {
            nextPushSettings[leafId] = true;
          }
        });

        return nextPushSettings;
      });

      return nextSettings;
    });
  };

  const handleTogglePush = (settingId: string) => {
    setPushSettings((current) => {
      return {
        ...current,
        [settingId]: !current[settingId],
      };
    });
  };

  const handleResetToDefault = () => {
    if (!customer || isSavingChanges) {
      return;
    }

    void (async () => {
      setIsSavingChanges(true);

      try {
        await resetNotificationTypesMutation.mutateAsync();
        const refetchResult = await notificationTypesQuery.refetch();
        applyServerData(refetchResult.data);
      } finally {
        setIsSavingChanges(false);
      }
    })();
  };

  const handleSave = () => {
    if (!customer || !hasPendingChanges || isSavingChanges) {
      return;
    }

    void (async () => {
      setIsSavingChanges(true);

      try {
        const updates = buildNotificationTypeUpdates(
          notificationSettings,
          pushSettings
        );
        await saveNotificationTypesMutation.mutateAsync(updates);

        const refetchResult = await notificationTypesQuery.refetch();
        applyServerData(refetchResult.data);
        onClose();
      } finally {
        setIsSavingChanges(false);
      }
    })();
  };

  const visibleSettingItems = flatNotificationSettingItems.filter(
    (item) => item.level === 1
  );

  return (
    <Modal visible={isOpen} onDismiss={onClose} variant="no-padding">
      <div className={styles["settings-modal"]}>
        <div className={styles["settings-modal-header"]}>
          <h3>Notification settings</h3>
          <button
            type="button"
            className={styles["settings-close-button"]}
            onClick={onClose}
            aria-label="Close notification settings"
          >
            <Icon name="Close" size="1rem"></Icon>
          </button>
        </div>
        <div className={styles["settings-list"]}>
          {notificationTypesQuery.isLoading ? (
            <div className={styles.listState}>
              Loading notification settings...
            </div>
          ) : notificationTypesQuery.isError ? (
            <div className={styles.listState}>
              Could not load notification settings.
            </div>
          ) : visibleSettingItems.length === 0 ? (
            <div className={styles.listState}>No notification settings.</div>
          ) : (
            <>
              <SettingsRow
                label="Enable all notifications"
                state={allNotificationsState}
                subtitle={allNotificationsSubtitle}
                showExpandControl={false}
                pushControl={
                  <span className={styles["push-column-empty"]}></span>
                }
                onToggle={handleToggleAllNotifications}
              />
              <div className={styles["settings-columns"]}>
                <span>Notifications</span>
                <span>Push</span>
              </div>
              {visibleSettingItems.map((settingItem) => (
                <div key={settingItem.id} className={styles["settings-item"]}>
                  <SettingsRow
                    label={settingItem.label}
                    level={settingItem.level}
                    state={notificationSettings[settingItem.id]}
                    hasChildren={false}
                    onToggle={() =>
                      handleToggleNotificationSetting(settingItem.id)
                    }
                    pushControl={
                      settingItem.hasChildren ? (
                        <span className={styles["push-column-empty"]}></span>
                      ) : (
                        <button
                          type="button"
                          className={styles["settings-toggle-button"]}
                          disabled={
                            notificationSettings[settingItem.id] === "disabled"
                          }
                          onClick={() => handleTogglePush(settingItem.id)}
                        >
                          <span
                            className={
                              styles["settings-toggle"] +
                              (pushSettings[settingItem.id]
                                ? ` ${styles["settings-toggle-enabled"]}`
                                : "")
                            }
                          >
                            <span className={styles["settings-toggle-thumb"]}>
                              {pushSettings[settingItem.id] && (
                                <Icon name="Check_mark" size="0.75rem"></Icon>
                              )}
                            </span>
                          </span>
                        </button>
                      )
                    }
                  />
                </div>
              ))}
            </>
          )}
        </div>
        <div className={styles["settings-modal-footer"]}>
          <button
            type="button"
            className={styles["settings-reset-button"]}
            onClick={handleResetToDefault}
            disabled={
              !canResetToDefault ||
              isSavingChanges ||
              resetNotificationTypesMutation.isPending ||
              saveNotificationTypesMutation.isPending
            }
          >
            Reset to default
          </button>
          <button
            type="button"
            className={styles["settings-save-button"]}
            onClick={handleSave}
            disabled={
              !hasPendingChanges ||
              isSavingChanges ||
              resetNotificationTypesMutation.isPending ||
              saveNotificationTypesMutation.isPending
            }
          >
            {isSavingChanges ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

type SettingsRowProps = {
  label: string;
  level?: number;
  state: SettingState;
  subtitle?: string;
  hasChildren?: boolean;
  expanded?: boolean;
  showExpandControl?: boolean;
  pushControl?: ReactNode;
  onToggleExpand?: () => void;
  onToggle: () => void;
};

function SettingsRow({
  label,
  level = 0,
  state,
  subtitle,
  hasChildren = false,
  expanded = true,
  showExpandControl = true,
  pushControl,
  onToggleExpand,
  onToggle,
}: SettingsRowProps) {
  const isEnabled = state === "enabled";
  const isPartial = state === "partial";

  return (
    <div
      className={styles["settings-row"]}
      style={{ paddingLeft: `${level * 1.5}rem` }}
    >
      <div className={styles["settings-row-main"]}>
        {showExpandControl ? (
          hasChildren ? (
            <button
              type="button"
              className={styles["settings-expand-button"]}
              onClick={(event) => {
                event.stopPropagation();
                onToggleExpand && onToggleExpand();
              }}
              aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
            >
              <Icon
                name={expanded ? "Caret_up" : "Caret_down"}
                size="0.875rem"
              ></Icon>
            </button>
          ) : (
            <span className={styles["settings-expand-spacer"]}></span>
          )
        ) : null}
        <button
          type="button"
          className={styles["settings-toggle-button"]}
          onClick={onToggle}
        >
          <span
            className={
              styles["settings-toggle"] +
              (isEnabled ? ` ${styles["settings-toggle-enabled"]}` : "") +
              (isPartial ? ` ${styles["settings-toggle-partial"]}` : "")
            }
          >
            <span className={styles["settings-toggle-thumb"]}>
              {isEnabled && <Icon name="Check_mark" size="0.75rem"></Icon>}
              {isPartial && (
                <span className={styles["settings-toggle-partial-mark"]}></span>
              )}
            </span>
          </span>
          <span className={styles["settings-row-label-group"]}>
            <span className={styles["settings-row-label"]}>{label}</span>
            {subtitle && (
              <span className={styles["settings-row-subtitle"]}>
                {subtitle}
              </span>
            )}
          </span>
        </button>
      </div>
      {pushControl && (
        <div className={styles["settings-row-push"]}>{pushControl}</div>
      )}
    </div>
  );
}

export default NotificationSettings;
